import { z } from 'zod'
import {
  LocalizedMessageDto,
  LocalizedMessageWireDto,
  UTCInstant,
} from './localizedMessage'

export const MonocleJobStatus = z.enum([
  'awaiting_inputs',
  'queued',
  'running',
  'retry_wait',
  'cancelling',
  'succeeded',
  'failed',
  'cancelled',
  'timed_out',
  'superseded',
])
export type MonocleJobState = z.infer<typeof MonocleJobStatus>
export const TerminalJobStates: readonly MonocleJobState[] = [
  'succeeded',
  'failed',
  'cancelled',
  'timed_out',
  'superseded',
]
export const isTerminalJob = (state: MonocleJobState) =>
  TerminalJobStates.includes(state)
export const JobIdentifier = z
  .string()
  .min(1)
  .max(160)
  .regex(/^[A-Za-z0-9_-]+$/)
export const MonocleJobsRollout = z.object({
  legacy_submissions_enabled: z.boolean().default(true),
  accepting_jobs: z.boolean().default(true),
  claims_enabled: z.boolean().default(true),
})
export const RecipeReference = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9_.-]{0,99}$/),
    version: z.number().int().positive(),
  })
  .strict()
export const RunnerSelector = z
  .object({
    pool: JobIdentifier,
    labels: z.array(JobIdentifier).max(16).default([]),
  })
  .strict()
export const JobInputReference = z.object({ bundle_id: JobIdentifier }).strict()
export const JobCreateDto = z
  .object({
    request_id: JobIdentifier,
    recipe: RecipeReference,
    runner: RunnerSelector,
    inputs: z
      .record(z.string().regex(/^[a-z][a-z0-9_]{0,63}$/), JobInputReference)
      .refine(
        (inputs) =>
          Object.keys(inputs).length > 0 && Object.keys(inputs).length <= 8,
      ),
    parameters: z.record(z.unknown()),
  })
  .strict()
export type JobCreate = z.infer<typeof JobCreateDto>

export const JobFilePin = z
  .object({
    bundle_id: JobIdentifier,
    keypath: z.string().min(1).max(160),
    path: z.string().min(1).max(1024),
    generation: z.string().regex(/^[0-9]+$/),
    size: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    md5: z.string().min(1).max(128),
  })
  .strict()
export type PinnedJobFile = z.infer<typeof JobFilePin>
export const JobErrorDto = z
  .object({
    code: JobIdentifier,
    retryable: z.boolean(),
    message: LocalizedMessageDto,
  })
  .strict()
export const JobOutputRole = z.enum([
  'glb',
  'usdz',
  'poster_jpg',
  'poster_mask_png',
])
export const JobOutputFile = z
  .object({
    role: JobOutputRole,
    path: z.string().min(1).max(1024),
    generation: z.string().regex(/^[0-9]+$/),
    size: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    md5: z.string().min(1).max(128),
  })
  .strict()
export const JobResultManifest = z
  .object({
    files: z.array(JobOutputFile).min(1).max(4),
    min_coordinate: z.array(z.number().finite()).length(3).optional(),
    max_coordinate: z.array(z.number().finite()).length(3).optional(),
  })
  .strict()
  .refine(
    (manifest) =>
      new Set(manifest.files.map((file) => file.role)).size ===
      manifest.files.length,
    'Duplicate output role',
  )

export const JobProgressDto = z
  .object({
    sequence: z.number().int().positive(),
    progress_percentage: z.number().int().min(0).max(99).nullable().optional(),
    progress_message: LocalizedMessageDto.nullable().optional(),
    stage: z.string().max(100).optional(),
  })
  .strict()
export const JobLeaseDto = z
  .object({
    attempt: z.number().int().positive(),
    claim_token: z.string().min(32).max(256),
  })
  .strict()
export const JobAttemptUpdateDto = JobLeaseDto.extend({
  progress: JobProgressDto.optional(),
}).strict()
export const JobCompletionDto = JobLeaseDto.extend({
  manifest: JobResultManifest,
}).strict()
export const JobFailureDto = JobLeaseDto.extend({ error: JobErrorDto }).strict()
export const RunnerCapabilitiesDto = z
  .object({
    platform: z.enum(['darwin', 'linux', 'win32']),
    tools: z.record(JobIdentifier, z.string().min(1).max(80)),
    recipes: z.array(RecipeReference).min(1).max(32),
    capacity: z.literal(1),
  })
  .strict()

export const MonocleJobDto = z
  .object({
    id: JobIdentifier,
    owner: JobIdentifier,
    request_id: JobIdentifier,
    thread_id: JobIdentifier,
    recipe: RecipeReference,
    runner: RunnerSelector,
    inputs: JobCreateDto.shape.inputs,
    parameters: z.record(z.unknown()),
    source_bundle_ids: z.array(JobIdentifier).min(1).max(8),
    result_bundle_id: JobIdentifier.optional(),
    status: MonocleJobStatus,
    created_at: UTCInstant,
    updated_at: UTCInstant,
    timeout_seconds: z.number().int().positive(),
    attempt: z.number().int().nonnegative(),
    deadline_at: UTCInstant.optional(),
    next_attempt_at: UTCInstant.optional(),
    cancellation_deadline_at: UTCInstant.optional(),
    progress_percentage: z.number().int().min(0).max(100).nullable().optional(),
    progress_message: LocalizedMessageWireDto.nullable().optional(),
    progress_sequence: z.number().int().nonnegative(),
    stage: z.string().optional(),
    error: JobErrorDto.extend({ message: LocalizedMessageWireDto }).optional(),
  })
  .strict()
export type MonocleJob = z.infer<typeof MonocleJobDto>
export const MonocleJobSummaryDto = MonocleJobDto.pick({
  id: true,
  recipe: true,
  status: true,
  attempt: true,
  progress_percentage: true,
  progress_message: true,
  updated_at: true,
  result_bundle_id: true,
})
export function jobSummary(job: MonocleJob) {
  return MonocleJobSummaryDto.parse(
    Object.fromEntries(
      Object.keys(MonocleJobSummaryDto.shape)
        .filter((key) => key in job)
        .map((key) => [key, job[key as keyof MonocleJob]]),
    ),
  )
}
export const MonocleJobDBModel = MonocleJobDto.extend({
  progress_message: LocalizedMessageDto.nullable().optional(),
  error: JobErrorDto.optional(),
  request_fingerprint: z.string().length(64),
  progress_high_watermark: z.number().int().min(0).max(99).optional(),
  pinned_inputs: z.array(JobFilePin).max(32),
  max_attempts: z.number().int().min(1).max(10),
  attempts: z
    .array(
      z.object({
        attempt: z.number().int().positive(),
        runner_id: JobIdentifier,
        started_at: UTCInstant,
        platform: z.string(),
        tools: z.record(z.string()),
        recipe: RecipeReference,
      }),
    )
    .max(10)
    .default([]),
  runner_id: JobIdentifier.optional(),
  claim_token_hash: z.string().length(64).optional(),
  lease_expires_at: UTCInstant.optional(),
  heartbeat_at: UTCInstant.optional(),
  first_started_at: UTCInstant.optional(),
  delete_requested: z.boolean().default(false),
  superseded: z.boolean().default(false),
  manifest: JobResultManifest.optional(),
}).strict()
export type MonocleJobRecord = z.infer<typeof MonocleJobDBModel>
export function publicMonocleJob(job: MonocleJobRecord): MonocleJob {
  return MonocleJobDto.parse(
    Object.fromEntries(
      Object.keys(MonocleJobDto.shape)
        .filter((key) => key in job)
        .map((key) => [key, job[key as keyof MonocleJobRecord]]),
    ),
  )
}

export function measuredProgress(fraction: number): number {
  if (!Number.isFinite(fraction) || fraction < 0 || fraction > 1)
    throw new Error('Progress must be a fraction from 0 to 1')
  return Math.min(99, Math.floor(fraction * 100))
}

export const PrimeSourceRetentionMilliseconds = 30 * 24 * 60 * 60 * 1000
export const JobLeaseMilliseconds = 120_000
export const JobCancellationGraceMilliseconds = 30_000
